import { useCallback, useEffect, useMemo, useState } from 'react'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import Modal from '@mui/material/Modal'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslate, languageState } from '@repo/unicity'
import { feelgreatCountriesAtom, countryState, type TCountryFeelgreat } from '@/core/store/country.store'
import { checkoutReferralInfoAtom } from '@/core/checkout/checkout.store'
import { CountryItem } from './country-item.component'
import { TabElement } from './tab-element.component'
import { CollapseSelectCountry } from './collapse-select-country.component'

type ContinentGroup = { continent: string; list: TCountryFeelgreat[] }

interface SelectCountryModalProps {
    open: boolean
    onClose: () => void
}

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    height: { xs: '100%', sm: '80%' },
    width: { xs: '100%', sm: '80%' },
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: { xs: '0px', sm: '8px' },
    border: 'none',
    '&:focus': { outline: 'none' },
    overflowY: 'scroll',
    '::-webkit-scrollbar': { display: 'none' }
}

function shouldHideCountry(country: TCountryFeelgreat): boolean {
    const mm = country.maintenance_mode
    if (!mm?.maintenance_mode) return false
    const text = mm as unknown as { text?: { english?: string; native?: string } }
    if (/developing/.test(text.text?.english ?? '') || /developing/.test(text.text?.native ?? '')) {
        if (/fg\.unicity\.com/.test(window.location.hostname)) return true
    }
    return false
}

export const SelectCountryModal = ({ open, onClose }: SelectCountryModalProps) => {
    const [tabValue, setTabValue] = useState(0)

    const fgCountries = useAtomValue(feelgreatCountriesAtom)
    const currentCountry = useAtomValue(countryState)
    const referralInfo = useAtomValue(checkoutReferralInfoAtom)
    const setLanguage = useSetAtom(languageState)
    const { translate } = useTranslate()

    const isMobile = useMediaQuery('(max-width:899px)')

    const continentGroups: ContinentGroup[] = useMemo(() => {
        if (!fgCountries?.data) return []
        return Object.entries(fgCountries.data)
            .filter(([, list]) => Array.isArray(list))
            .map(([continent, list]) => ({ continent, list: list as TCountryFeelgreat[] }))
    }, [fgCountries])

    useEffect(() => {
        if (!open || continentGroups.length === 0) return
        const idx = continentGroups.findIndex(group => group.list.some(item => item.short === currentCountry.short))
        if (idx >= 0) setTabValue(idx)
    }, [open, continentGroups, currentCountry.short])

    const handleClickChangeLanguage = useCallback(
        (country: TCountryFeelgreat, languageCode: string, defaultLang = '') => {
            const lang = defaultLang || languageCode
            const shortCountry = (country.short || '').toLowerCase()
            const shortCurrentCountry = (currentCountry.short || '').toLowerCase()

            if (shortCountry === shortCurrentCountry) {
                const url = new URL(window.location.href)
                url.searchParams.set('lang', lang.toLowerCase())
                window.history.pushState({}, '', url.toString())
                try {
                    localStorage.setItem('fg_language', lang.toLowerCase())
                } catch {
                    /* ignore */
                }
                setLanguage(lang.toUpperCase())
                onClose()
                return
            }

            const envKey = window.location.hostname === 'fg.unicity.com' ? 'live' : 'test'
            const urlTemplate = country[envKey as keyof TCountryFeelgreat] as string
            if (!urlTemplate) {
                onClose()
                return
            }

            const isExternal = urlTemplate.startsWith('https')
            const countryCode = country.short || ''
            // apps/fg runs on u-feelgreat.unicity.com but country links target the fg.unicity.com domain.
            const domain = 'https://fg.unicity.com'

            let resultUrl = urlTemplate
                .replace(/\{{2,3}\s*domain\s*\}{2,3}/g, domain)
                .replace(/\{{2,3}\s*country\s*\}{2,3}/g, countryCode.toLowerCase())
                .replace(/\{{2,3}\s*language\s*\}{2,3}/g, isExternal ? lang.toLowerCase() : `?lang=${lang.toLowerCase()}`)

            if (referralInfo?.baId) {
                const baId = referralInfo.baId
                if (/\?/.test(resultUrl)) {
                    const parts = resultUrl.split('?')
                    resultUrl = `${parts[0]}/${baId}?${parts[1]}`
                } else if (/home/.test(resultUrl)) {
                    resultUrl = `${resultUrl.replace('home', 'c')}/${baId}`
                } else {
                    resultUrl = `${resultUrl}/${baId}`
                }
            }

            window.open(resultUrl, isExternal ? '_blank' : '_self')
        },
        [currentCountry.short, referralInfo, setLanguage, onClose]
    )

    const a11yProps = (index: number) => ({
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`
    })

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{ backdrop: { timeout: 350 } }}
            data-testid="select-country-modal-modal">
            <Fade in={open} data-testid="select-country-modal-fade">
                <Box sx={modalStyle} data-testid="select-country-modal-box-on-close-1">
                    <Stack
                        sx={{
                            position: 'relative',
                            padding: '50px 30px',
                            overflowY: 'scroll',
                            '::-webkit-scrollbar': { display: 'none' }
                        }}
                        data-testid="select-country-modal-stack-on-close">
                        {/* Close button */}
                        <Box
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: 15,
                                right: 15,
                                cursor: 'pointer',
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            data-testid="select-country-modal-box-on-close-2">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-testid="select-country-modal-svg-1">
                                <path
                                    d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
                                    fill="#153862"
                                    data-testid="select-country-modal-path-1"
                                />
                            </svg>
                        </Box>

                        {/* Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'space-between',
                                width: '100%',
                                mb: { xs: '0px', md: '30px' }
                            }}
                            data-testid="select-country-modal-box-typography-select-country-language">
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '18px', md: '20px', lg: '24px' },
                                    color: '#153862',
                                    fontStyle: 'italic',
                                    fontWeight: 700,
                                    textAlign: 'left',
                                    mb: { xs: '20px', md: '0' }
                                }}
                                data-testid="select-country-modal-typography-select-country-language">
                                {translate('select_country_language')}
                            </Typography>
                            <Box
                                sx={{
                                    maxWidth: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: '#E9F1FA',
                                    padding: '8px 17px',
                                    borderRadius: '6px',
                                    '@media (min-width: 1024px)': { maxWidth: '500px' }
                                }}
                                data-testid="select-country-modal-box-svg-1">
                                <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }} data-testid="select-country-modal-box-svg-2">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-testid="select-country-modal-svg-2">
                                        <circle cx="8" cy="8" r="7.5" stroke="#153862" data-testid="select-country-modal-circle" />
                                        <path d="M8 4V5" stroke="#153862" strokeLinecap="round" data-testid="select-country-modal-path-2" />
                                        <path d="M8 7V12" stroke="#153862" strokeLinecap="round" data-testid="select-country-modal-path-3" />
                                    </svg>
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: { xs: '12px', md: '10px', lg: '12px' },
                                        color: '#153862'
                                    }}
                                    data-testid="select-country-modal-typography-change-country-description">
                                    {translate('change_country_description')}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Content */}
                        {isMobile ? (
                            <CollapseSelectCountry
                                data={continentGroups}
                                value={tabValue}
                                setValue={v => setTabValue(v ?? 0)}
                                handleClickChangeCountry={handleClickChangeLanguage}
                                translate={translate}
                                data-testid="select-country-modal-collapse-select-country"
                            />
                        ) : (
                            <TabElement data-testid="select-country-modal-tab-element">
                                <Box className="tab-bar" data-testid="select-country-modal-box-tabs">
                                    <Tabs value={tabValue} onChange={(_, index) => setTabValue(index)} data-testid="select-country-modal-tabs">
                                        {continentGroups.map((e, index) => (
                                            <Tab
                                                label={translate(e.continent)}
                                                {...a11yProps(index)}
                                                key={`tab-${index}`}
                                                sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                                                data-testid="select-country-modal-tab"
                                            />
                                        ))}
                                    </Tabs>
                                </Box>
                                {continentGroups.map((e, index) => (
                                    <TabElement.TabPanel
                                        value={tabValue}
                                        index={index}
                                        key={`tab-content-${index}`}
                                        data-testid="select-country-modal-tab-element-tab-panel">
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: '1fr',
                                                    sm: '1fr 1fr',
                                                    md: '1fr 1fr 1fr',
                                                    lg: '1fr 1fr 1fr 1fr'
                                                },
                                                gap: '20px',
                                                height: '100%',
                                                minWidth: '100%'
                                            }}
                                            data-testid="select-country-modal-box">
                                            {tabValue === index &&
                                                e.list.map((country, idx) => {
                                                    if (shouldHideCountry(country)) return null
                                                    return (
                                                        <CountryItem
                                                            country={country}
                                                            key={`country-${idx}`}
                                                            handleClick={handleClickChangeLanguage}
                                                            data-testid="select-country-modal-country-item"
                                                        />
                                                    )
                                                })}
                                        </Box>
                                    </TabElement.TabPanel>
                                ))}
                            </TabElement>
                        )}
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    )
}
